/**
 * Service do Mapa da Produção. Cada função recebe `request` (do useAuth)
 * como 1º argumento e devolve o JSON da resposta.
 *
 * O que vem do backend: os SETORES cadastrados (com a forma desenhada no
 * mapa), subsetores, postos, colaboradores, a alocação de cada ambiente, as
 * tarefas, as PEÇAS DESMEMBRADAS e os eventos. O que vem da planilha
 * (`useDados`): os ambientes e a ETAPA de cada um — é a etapa que decide para
 * qual setor a planilha empurra o ambiente (`setores.js`).
 *
 * `silentError`: quem chama mostra a mensagem do backend (ex.: o 409
 * "Alterado por Fulano em 22/09 14:03. Recarregue.") — sem isso sairiam dois
 * avisos iguais. O erro lançado já traz a mensagem do backend em
 * `err.message` e o código em `err.status` (buildError do AuthContext).
 */
const PREFIXO = "/api/mapa-producao";

function comConsulta(caminho, filtros = {}) {
  const consulta = new URLSearchParams();
  Object.entries(filtros).forEach(([campo, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") consulta.set(campo, String(valor));
  });
  const texto = consulta.toString();
  return texto ? `${caminho}?${texto}` : caminho;
}

function ler(request, caminho, filtros) {
  return request(comConsulta(`${PREFIXO}${caminho}`, filtros), { silentError: true });
}

function escrever(request, metodo, caminho, corpo) {
  return request(`${PREFIXO}${caminho}`, {
    method: metodo,
    body: JSON.stringify(corpo ?? {}),
    silentError: true,
  });
}

function apagar(request, caminho, versao) {
  return request(comConsulta(`${PREFIXO}${caminho}`, { versao }), {
    method: "DELETE",
    silentError: true,
  });
}

const id = (valor) => encodeURIComponent(String(valor));

/**
 * O vínculo do setor com a planilha chama-se `etapa_planilha` na API e `etapa`
 * dentro do Mapa (ao lado de `demanda.etapa`, que é a etapa do ambiente). A
 * tradução mora AQUI, no único lugar que fala com o backend: chave errada em
 * JS não dá erro, dá `undefined` — foi assim que, na primeira subida da v3,
 * TODA sala apareceu como "Sem etapa da planilha" e os 117 ambientes caíram na
 * bandeja, sem uma única mensagem de erro.
 */
function setorDoServidor(setor) {
  if (!setor || typeof setor !== "object") return setor;
  const { etapa_planilha: etapa, ...resto } = setor;
  return { ...comFormaDoServidor(resto), etapa: etapa || null };
}

function setorParaServidor(dados) {
  const corpo = comFormaParaServidor(dados);
  if (!corpo || typeof corpo !== "object" || !("etapa" in corpo)) return corpo;
  const { etapa, ...resto } = corpo;
  return { ...resto, etapa_planilha: etapa };
}

/**
 * O DESENHO viaja em dois dialetos: dentro do Mapa um ponto é `{x, y}` (é
 * assim que `dados/forma.js` calcula, move e desenha) e na API é o par
 * `[x, y]`. A conversão mora aqui, ao lado da de `etapa`, pelo mesmo motivo:
 * mandar o dialeto errado não dá erro de código — deu 400 "Cada ponto precisa
 * ser um par [x, y]" na cara de quem acabou de riscar a sala com o dedo.
 *
 * Só o polígono tem pontos; retângulo e elipse passam intactos.
 */
function pontosParaServidor(pontos) {
  return pontos.map((ponto) => (Array.isArray(ponto) ? ponto : [ponto.x, ponto.y]));
}

function pontosDoServidor(pontos) {
  return pontos.map((ponto) => (Array.isArray(ponto) ? { x: ponto[0], y: ponto[1] } : ponto));
}

function converterForma(forma, converter) {
  if (!forma || typeof forma !== "object" || !Array.isArray(forma.pontos)) return forma;
  return { ...forma, pontos: converter(forma.pontos) };
}

function comFormaParaServidor(dados) {
  if (!dados || typeof dados !== "object" || !("forma" in dados)) return dados;
  return { ...dados, forma: converterForma(dados.forma, pontosParaServidor) };
}

function comFormaDoServidor(item) {
  if (!item || typeof item !== "object" || !("forma" in item)) return item;
  return { ...item, forma: converterForma(item.forma, pontosDoServidor) };
}

const listaDoServidor = (valor) => (Array.isArray(valor) ? valor.map(comFormaDoServidor) : valor);

/**
 * 409 = alguém alterou antes (versão divergente) ou a transição não vale no
 * estado atual. Nos dois casos a tela mostra a mensagem e recarrega.
 */
export function ehConflito(erro) {
  return erro?.status === 409;
}

// ------------------------------------------------------------- estado

/**
 * Tudo o que o app guarda, numa leitura só (o polling de 15 s usa esta):
 * {gerado_em, setores, subsetores, postos, colaboradores, alocacoes, tarefas,
 * pecas}. É daqui que sai o DESENHO do mapa — cada setor, subsetor e posto
 * traz a própria `forma` (`dados/forma.js`).
 */
export async function obterEstadoMapa(request) {
  const estado = await ler(request, "/estado");
  if (!Array.isArray(estado?.setores)) return estado;
  return {
    ...estado,
    setores: estado.setores.map(setorDoServidor),
    subsetores: listaDoServidor(estado.subsetores),
    postos: listaDoServidor(estado.postos),
  };
}

// ------------------------------------------------------------ setores

export async function listarSetores(request) {
  const setores = await ler(request, "/setores");
  return Array.isArray(setores) ? setores.map(setorDoServidor) : setores;
}

/**
 * Cria um setor desenhado no mapa. `dados` =
 * {nome, etapa, recebe_da_planilha, forma, ordem}:
 * - `etapa`: código do catálogo da planilha (`dados/etapasPlanilha.js`) — é o
 *   vínculo que faz os ambientes daquela etapa chegarem aqui;
 * - `recebe_da_planilha`: marca ESTE setor como o que recebe os ambientes da
 *   etapa (vários setores podem apontar para a mesma etapa; só um recebe);
 * - `forma`: {tipo, x, y, largura, altura, pontos} de `dados/forma.js`.
 */
export async function criarSetor(request, dados) {
  return setorDoServidor(await escrever(request, "POST", "/setores", setorParaServidor(dados)));
}

/**
 * `dados` = campos editáveis (nome, etapa, recebe_da_planilha, forma, ordem,
 * ativo) + `versao` (obrigatória: é a trava otimista do cadastro).
 */
export async function atualizarSetor(request, setorId, dados) {
  const corpo = setorParaServidor(dados);
  return setorDoServidor(await escrever(request, "PATCH", `/setores/${id(setorId)}`, corpo));
}

/**
 * Apaga de verdade uma sala que NUNCA carregou trabalho (sem ambiente, tarefa,
 * peça, subsetor ou posto na história dela) — é o desfazer de quem desenhou
 * errado. Sala que já foi usada o backend recusa com 400 dizendo para
 * desativar; a história nunca é apagada junto.
 *
 * `versao` é obrigatória (trava otimista) e vai na query: 409 = alguém mexeu.
 */
export function excluirSetor(request, setorId, versao) {
  return apagar(request, `/setores/${id(setorId)}`, versao);
}

// -------------------------------------------------------------- formas

/** Alvos que têm forma no mapa (setor > subsetor > posto). */
export const ALVO_FORMA = Object.freeze({
  SETOR: "setor",
  SUBSETOR: "subsetor",
  POSTO: "posto",
});

/**
 * Grava de uma vez o desenho de vários elementos — é o que o gestor produz ao
 * arrastar/redimensionar e soltar. `formas` = [{alvo, id, forma, versao}],
 * `alvo` ∈ ALVO_FORMA e `versao` a do elemento que a tela carregou.
 *
 * Tudo ou nada: se UMA versão divergir, o backend responde 409 sem gravar
 * nada (dois gestores arrastando a mesma sala não podem se sobrescrever pela
 * metade). A resposta traz os elementos atualizados, já com a versão nova.
 */
export async function salvarFormas(request, formas) {
  const corpo = formas.map((item) => comFormaParaServidor(item));
  const resposta = await escrever(request, "PUT", "/formas", { formas: corpo });
  if (!Array.isArray(resposta?.atualizados)) return resposta;
  return { ...resposta, atualizados: resposta.atualizados.map(comFormaDoServidor) };
}

// ---------------------------------------------------------- cadastros

export async function listarSubsetores(request) {
  return listaDoServidor(await ler(request, "/subsetores"));
}

/** `dados` = {setor_id, nome, ordem, forma}. */
export async function criarSubsetor(request, dados) {
  return comFormaDoServidor(await escrever(request, "POST", "/subsetores", comFormaParaServidor(dados)));
}

/** `dados` = {setor_id, nome, ordem, forma, ativo} (parcial) + `versao`. */
export async function atualizarSubsetor(request, subsetorId, dados) {
  const corpo = comFormaParaServidor(dados);
  return comFormaDoServidor(await escrever(request, "PATCH", `/subsetores/${id(subsetorId)}`, corpo));
}

/** Apaga o subsetor recém-desenhado (mesma regra do setor: só o que nunca foi usado). */
export function excluirSubsetor(request, subsetorId, versao) {
  return apagar(request, `/subsetores/${id(subsetorId)}`, versao);
}

export function listarColaboradores(request) {
  return ler(request, "/colaboradores");
}

/** `dados` = {nome, apelido_planilha, setor_ids: [], ativo}. */
export function criarColaborador(request, dados) {
  return escrever(request, "POST", "/colaboradores", dados);
}

export function atualizarColaborador(request, colaboradorId, dados) {
  return escrever(request, "PATCH", `/colaboradores/${id(colaboradorId)}`, dados);
}

/**
 * Apaga o colaborador cadastrado por engano (só quem nunca teve tarefa ou
 * peça). Sem versão: o cadastro da pessoa não é desenhável.
 */
export function excluirColaborador(request, colaboradorId) {
  return apagar(request, `/colaboradores/${id(colaboradorId)}`);
}

export async function listarPostos(request) {
  return listaDoServidor(await ler(request, "/postos"));
}

/** `dados` = {setor_id, subsetor_id | null, nome, colaborador_ids: [], forma}. */
export async function criarPosto(request, dados) {
  return comFormaDoServidor(await escrever(request, "POST", "/postos", comFormaParaServidor(dados)));
}

export async function atualizarPosto(request, postoId, dados) {
  const corpo = comFormaParaServidor(dados);
  return comFormaDoServidor(await escrever(request, "PATCH", `/postos/${id(postoId)}`, corpo));
}

/** Apaga o posto recém-desenhado (mesma regra: só o que nunca foi usado). */
export function excluirPosto(request, postoId, versao) {
  return apagar(request, `/postos/${id(postoId)}`, versao);
}

// ------------------------------------------------------------ tarefas

/**
 * `filtros` = {setor_id, status, ambiente_chave, peca_id, colaborador_id,
 * posto_id} (todos opcionais).
 */
export function listarTarefas(request, filtros = {}) {
  return ler(request, "/tarefas", filtros);
}

/**
 * `dados` = {setor_id, subsetor_id, posto_id, titulo, descricao,
 * colaborador_id, ambiente_chave, ambiente_rotulo, peca_id, data_prevista,
 * data_limite}. `peca_id` pendura a tarefa numa peça desmembrada.
 */
export function criarTarefa(request, dados) {
  return escrever(request, "POST", "/tarefas", dados);
}

/** `dados` = campos editáveis + `versao` (obrigatória) + `motivo` (opcional). */
export function atualizarTarefa(request, tarefaId, dados) {
  return escrever(request, "PATCH", `/tarefas/${id(tarefaId)}`, dados);
}

/** `acao` ∈ iniciar | pausar | retomar | concluir | cancelar | reabrir. */
export function executarAcaoTarefa(request, tarefaId, { acao, versao, motivo = "" }) {
  return escrever(request, "POST", `/tarefas/${id(tarefaId)}/acao`, { acao, versao, motivo });
}

export function comentarTarefa(request, tarefaId, texto) {
  return escrever(request, "POST", `/tarefas/${id(tarefaId)}/comentarios`, { texto });
}

export function listarEventosTarefa(request, tarefaId) {
  return ler(request, `/tarefas/${id(tarefaId)}/eventos`);
}

// ---------------------------------------------------------- ambientes

/**
 * `dados` = {chave, rotulo, setor_id, etapa_origem, subsetor_id, posto_id,
 * versao} — versão 0 cria.
 *
 * `etapa_origem` é a etapa da planilha em que o ambiente estava quando o
 * gestor apontou (a tela manda `demanda.etapa`). É o que permite mover o
 * ambiente para um setor que não é o da etapa e ainda saber, na leitura
 * seguinte, se a planilha andou depois disso (`combinar.js`).
 */
export function alocarAmbiente(request, dados) {
  return escrever(request, "POST", "/ambientes/alocar", dados);
}

/**
 * `dados` = {chave, rotulo, setor_id, acao: iniciar|pausar|retomar|aguardar,
 * motivo, versao}.
 */
export function executarAcaoAmbiente(request, dados) {
  return escrever(request, "POST", "/ambientes/acao", dados);
}

/**
 * `dados` = {chave, rotulo, setor_id, data_prevista, data_limite, prioridade,
 * motivo, versao}. `prioridade` ∈ normal | alta | urgente — é o que a peça
 * desmembrada herda depois (`dados/pecas.js`).
 */
export function definirPrazosAmbiente(request, dados) {
  return escrever(request, "POST", "/ambientes/prazos", dados);
}

// ------------------------------------------- histórico de comentários
// Um só histórico por ambiente para o Mapa e as 16 páginas do Legado
// (`components/HistoricoAmbiente`). Editar/apagar: só o autor, 15 minutos.

/**
 * `dados` = {chave, rotulo, texto, etapa, pagina, setor_id?}. Devolve o
 * comentário: {id, chave, rotulo, texto, etapa, pagina, autor, autor_id,
 * criado_em, editado_em, editavel_ate, pode_alterar}.
 */
export function comentarAmbiente(request, dados) {
  return escrever(request, "POST", "/ambientes/comentarios", dados);
}

/** Comentários visíveis do ambiente (inclusive de nomes antigos), mais novos primeiro. */
export function listarComentariosAmbiente(request, chave) {
  return ler(request, "/ambientes/comentarios", { chave });
}

export function editarComentarioAmbiente(request, id, texto) {
  return escrever(request, "PATCH", `/ambientes/comentarios/${encodeURIComponent(id)}`, { texto });
}

export function apagarComentarioAmbiente(request, id) {
  return request(`${PREFIXO}/ambientes/comentarios/${encodeURIComponent(id)}`, {
    method: "DELETE",
    silentError: true,
  });
}

/** `{chave atual: total}` da unidade — o balão das listas (1 request por página). */
export function contarComentariosAmbientes(request) {
  return ler(request, "/ambientes/comentarios/contagem");
}

/**
 * Ambiente renomeado na planilha: `dados` = {de, para, rotulo}. Leva
 * alocação, tarefas e peças da chave velha para a nova (`gerir`); os
 * comentários seguem pela corrente de religações.
 * Devolve {de, para, alocacao, tarefas, pecas, comentarios}.
 */
export function religarAmbiente(request, dados) {
  return escrever(request, "POST", "/ambientes/religar", dados);
}

export function listarEventosAmbiente(request, chave) {
  return ler(request, "/ambientes/eventos", { chave });
}

// -------------------------------------------------------------- peças

/**
 * `filtros` = {ambiente_chave, setor_id, subsetor_id, posto_id,
 * colaborador_id, status} (todos opcionais; `status` aceita vários separados
 * por vírgula). Não há filtro "fora": peça fora é a que não foi encerrada, e
 * quem conta isso é `contarPecasForaPorAmbiente` sobre `/estado.pecas`.
 */
export function listarPecas(request, filtros = {}) {
  return ler(request, "/pecas", filtros);
}

/**
 * Desmembra uma peça do ambiente. `dados` = {ambiente_chave, ambiente_rotulo,
 * nome, setor_id, subsetor_id, posto_id, colaborador_id, prioridade,
 * data_prevista, data_limite}.
 *
 * Prioridade e prazos saem prontos de `herdarDoAmbiente` (`dados/pecas.js`):
 * a peça nasce com os do ambiente e o gestor altera se quiser.
 */
export function desmembrarPeca(request, dados) {
  return escrever(request, "POST", "/pecas", dados);
}

/**
 * `dados` = {nome, prioridade, ambiente_rotulo} + `versao` (obrigatória).
 *
 * SÓ estes três. Responsável é `alocarPeca` e prazo é `definirPrazosPeca` —
 * o backend ignora em silêncio o que vier fora do endereço, devolvendo 200
 * com a peça intacta (`setor/useAcoesDoSetor.salvarCamposDaPeca` encadeia as
 * três escritas quando o painel mexe em campos de mais de uma).
 */
export function atualizarPeca(request, pecaId, dados) {
  return escrever(request, "PATCH", `/pecas/${id(pecaId)}`, dados);
}

/**
 * `dados` = {setor_id, subsetor_id, posto_id, colaborador_id, versao} — a peça
 * anda sozinha. Campo omitido fica como está; trocar de setor limpa subsetor e
 * posto (o lugar da sala antiga não existe na nova).
 */
export function alocarPeca(request, pecaId, dados) {
  return escrever(request, "POST", `/pecas/${id(pecaId)}/alocar`, dados);
}

/**
 * `dados` = {data_prevista, data_limite, motivo, versao}. Campo ausente fica
 * como está; `null` limpa (e a peça volta a valer o prazo do ambiente).
 */
export function definirPrazosPeca(request, pecaId, dados) {
  return escrever(request, "POST", `/pecas/${id(pecaId)}/prazos`, dados);
}

/**
 * `acao` ∈ iniciar | pausar | retomar | aguardar | retornar | concluir |
 * cancelar | reabrir. `retornar` é o "voltou para o ambiente"; `concluir`
 * encerra sem retorno.
 */
export function executarAcaoPeca(request, pecaId, { acao, versao, motivo = "" }) {
  return escrever(request, "POST", `/pecas/${id(pecaId)}/acao`, { acao, versao, motivo });
}

export function comentarPeca(request, pecaId, texto) {
  return escrever(request, "POST", `/pecas/${id(pecaId)}/comentarios`, { texto });
}

export function listarEventosPeca(request, pecaId) {
  return ler(request, `/pecas/${id(pecaId)}/eventos`);
}

// ------------------------------------------------------------ eventos

/** `filtros` = {setor_id, desde (ISO), limite (padrão do backend: 200)}. */
export function listarEventosSetor(request, filtros = {}) {
  return ler(request, "/eventos", filtros);
}

// ------------------------------------------- projetista do executivo

/**
 * `{pode_atribuir, projetistas: [{id, nome}], atribuicoes: {chave: {projetista_id,
 * projetista, inicio, atribuido_por, atribuido_em, anteriores: [{projetista, inicio, fim}]}}}`
 * — uma request para a página inteira.
 */
export function lerProjetistasExecutivo(request) {
  return ler(request, "/executivo/projetistas");
}

/**
 * `itens` = [{chave, rotulo}]; `projetistaId` nulo retira; `inicio` "AAAA-MM-DD"
 * (vazio = hoje). Tudo ou nada. Devolve `{atribuicoes}` só dos ambientes enviados.
 */
export function atribuirProjetistaExecutivo(request, { itens, projetistaId, inicio }) {
  return escrever(request, "POST", "/executivo/projetistas", {
    itens,
    projetista_id: projetistaId || null,
    inicio: inicio || null,
  });
}

/** `{total}` — ambientes que outra pessoa passou para o usuário nos últimos dias (sino). */
export function contarAmbientesRecebidos(request) {
  return ler(request, "/executivo/projetistas/recebidos");
}

// ------------------------------------------- exceções de carga (capacidade)

/**
 * Carga de cada ambiente na marcenaria marcada pelo PCP (Capacidade da
 * Fábrica): `{pode_marcar, excecoes: {chave: {classe, motivo, observacao,
 * marcado_por, marcado_em, anteriores: [{classe, motivo, marcado_por, marcado_em}]}}}`.
 * Uma request para a página inteira.
 */
export function lerExcecoesCapacidade(request) {
  return ler(request, "/capacidade/excecoes");
}

/**
 * `itens` = [{chave, rotulo}] (1 a 300); `classe` ∈ leve | normal | pesado;
 * `motivo` ∈ curva | item_especial | volume_grande | outro (obrigatório no
 * pesado; nulo nos outros). Tudo ou nada. Devolve `{excecoes}` só dos enviados.
 */
export function marcarExcecoesCapacidade(request, { itens, classe, motivo, observacao }) {
  return escrever(request, "POST", "/capacidade/excecoes", {
    itens,
    classe,
    motivo: motivo || null,
    observacao: observacao || "",
  });
}
